// useAuth.js - supaprastintas failas, paliekant tik veiksmų funkcijas
import { useState, useCallback } from 'react'; 
import { supabase } from '../services/supabaseClient';

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Register function
  const registerUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: { // Data stored in auth.users.raw_user_meta_data
            imones_pavadinimas: userData.imonesPavadinimas,
            vardas: userData.vardas || '',
            pavarde: userData.pavarde || '',
            tel: userData.tel || '', // Ensure this key is used later if needed
            username: userData.username || userData.email
          },
          emailRedirectTo: `${window.location.origin}/email-confirmation`
        }
      });

      if (signUpError) {
          throw signUpError; // Rethrow error to be caught below
      }

      // If signUp successful, the 'handle_new_user' trigger in the DB
      // should automatically create the user profile in 'public.users' table.
      // Therefore, we no longer need to insert it manually from the frontend.
      if (signUpData.user) {
      } else {
      }

      // Return success only if both steps succeeded
      return { success: true, user: signUpData.user };

    } catch (err) {
      setError(`Registracijos klaida: ${err.message}`); // Set user-visible error
      return { success: false, error: err }; // Return failure status
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Check if it's a CORS error
        if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
          console.error('CORS error during login:', error);
          // Try alternative approach if CORS error
          throw new Error('Prisijungimo klaida. Bandykite dar kartą arba susisiekite su administratoriumi.');
        }
        throw error;
      }
      
      // --- Check for potential issues like unconfirmed email --- 
      let response = { success: true, user: data.user, session: data.session };
      
      // Check if user data is available and email needs confirmation
      if (data.user && !data.user.email_confirmed_at) {
         // Check if the profile exists and is marked as confirmed (though email isn't)
         // This scenario might indicate an admin override or different flow
         const { data: profileData, error: profileError } = await supabase
          .from('users') 
          .select('account_confirmed')
          .eq('id', data.user.id)
          .single();

         // If profile check fails or account is NOT confirmed, flag it
         if (profileError || !profileData?.account_confirmed) {
            console.warn("Login successful but email not confirmed.");
            response.warningType = 'EMAIL_NOT_CONFIRMED';
         }
      }

      // Return success, state will be updated by GlobalStates listener
      return response;
    } catch (err) {
      setError(`Prisijungimo klaida: ${err.message}`);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Return success, state will be updated by GlobalStates listener
      return { success: true };
    } catch (err) {
      setError(`Atsijungimo klaida: ${err.message}`);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  // Password reset request
  const resetPassword = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/NaujasSlaptazodis`,
      });

      if (error) throw error;
      return { success: true };
    } catch (err) {
      setError(`Slaptažodžio atstatymo klaida: ${err.message}`);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update password (when user is logged in)
  const updatePassword = useCallback(async (newPassword) => {
    setLoading(true);
    setError(null);
    try {
      // Pažymime, kad vyksta slaptažodžio keitimas, kad būtų išvengta netikėtų nukreipimų
      sessionStorage.setItem('password_reset_in_progress', 'true');
      
      // Atnaujinti slaptažodį ir palaukti rezultato
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      
      // Išsaugoti nuorodą į neseniai įvykusį slaptažodžio keitimą
      if (data && data.user) {
        localStorage.setItem('password_recently_changed', 'true');
      }
      
      // Return success, GlobalStates might react to USER_UPDATED event if needed
      return { success: true };
    } catch (err) {
      setError(`Slaptažodžio atnaujinimo klaida: ${err.message}`);
      return { success: false, error: err };
    } finally {
      // Užbaigus procesą, pašaliname žymę
      sessionStorage.removeItem('password_reset_in_progress');
      setLoading(false);
    }
  }, []);

  // Check account status - Keep this as it provides specific checks beyond basic auth state
  const checkAccountStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get current authenticated user directly
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

      if (authError || !authUser) {
        // If getUser fails or returns no user, they are not authenticated
        return { success: false, message: 'Vartotojas neprisijungęs', status: null };
      }

      // Call the consolidated RPC function to get all statuses
      const { data: statusData, error: rpcError } = await supabase.rpc('check_account_status', {
          user_id: authUser.id
      });

      if (rpcError) {
          console.error("RPC check_account_status error:", rpcError);
          setError('Vartotojo statuso patikrinimo klaida (RPC)');
          // Decide how to handle the error, e.g., throw or return specific status
          // For now, let's return an error state
          return { 
              success: false, 
              message: 'Nepavyko gauti vartotojo statuso.', 
              error: rpcError, 
              status: null 
          };
      }

      // Extract statuses from the first row of the result (RPC returns an array)
      // Provide defaults in case the RPC returns empty or unexpected data
      const statusResult = statusData?.[0] || { 
          email_confirmed: false, 
          admin_confirmed: false, 
          account_confirmed: false 
      };

      return {
        success: true,
        user: authUser, // Return the user object from auth
        status: {
          emailConfirmed: statusResult.email_confirmed,
          adminConfirmed: statusResult.admin_confirmed,
          accountConfirmed: statusResult.account_confirmed
        }
      };
    } catch (err) { // Catch unexpected errors (e.g., network issues)
      setError(`Statuso gavimo klaida: ${err.message}`);
      return { success: false, error: err, status: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Return only the action functions and loading/error states related to *these actions*
  return {
    loading, // Indicates if one of *these actions* is in progress
    error,   // Error from the last action performed by *this hook*
    registerUser,
    login,
    logout,
    resetPassword,
    updatePassword,
    checkAccountStatus
  };
};

export default useAuth;